import { useEffect, useState } from 'react';
import { Loader2, Search, Users } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import axiosInstance from '@/lib/axios';
import { useAdminMembers } from '@/hooks/use-admin-members';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import type { AvenueMemberSummary, CommunityAvenue } from '@/types';

interface AvenueMembersDialogProps {
  communityId: string;
  avenue: CommunityAvenue | null;
  assignedMembers: AvenueMemberSummary[];
  onClose: () => void;
  onSaved: () => void;
}

export function AvenueMembersDialog({
  communityId,
  avenue,
  assignedMembers,
  onClose,
  onSaved,
}: AvenueMembersDialogProps) {
  const [search, setSearch] = useState('');
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [isSaving, setIsSaving] = useState(false);

  const { members, isLoading } = useAdminMembers(communityId, {
    search,
    limit: 200,
  });

  useEffect(() => {
    if (!avenue) {
      setSelectedUserIds([]);
      setSearch('');
      return;
    }
    setSelectedUserIds(assignedMembers.map((member) => member.id));
  }, [avenue, assignedMembers]);

  const toggleMember = (userId: string, checked: boolean) => {
    setSelectedUserIds((current) =>
      checked ? [...new Set([...current, userId])] : current.filter((id) => id !== userId)
    );
  };

  const handleSave = async () => {
    if (!avenue) return;
    setIsSaving(true);
    try {
      await axiosInstance.put(`/api/communities/${communityId}/avenues/${avenue.id}/members`, {
        userIds: selectedUserIds,
      });
      toast.success('Avenue members updated');
      onSaved();
      onClose();
    } catch (error) {
      const message =
        axios.isAxiosError(error) &&
        error.response?.data &&
        typeof error.response.data === 'object' &&
        'error' in error.response.data
          ? String((error.response.data as { error: string }).error)
          : 'Failed to update avenue members';
      toast.error(message);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={!!avenue} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="h-5 w-5 text-primary" />
            Manage Members
          </DialogTitle>
          <DialogDescription>
            {avenue ? `Choose which members belong to the ${avenue.name} avenue.` : ''}
          </DialogDescription>
        </DialogHeader>

        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search members..."
            className="pl-9"
          />
        </div>

        {isLoading ? (
          <div className="flex justify-center py-10">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        ) : members.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4">No members found.</p>
        ) : (
          <div className="space-y-2 py-2 max-h-80 overflow-y-auto">
            {members.map((member) => (
              <div key={member.id} className="flex items-center gap-3 rounded-lg border px-4 py-3">
                <Checkbox
                  id={`avenue-member-${member.id}`}
                  checked={selectedUserIds.includes(member.id)}
                  onCheckedChange={(checked) => toggleMember(member.id, checked === true)}
                />
                <Avatar className="h-8 w-8 shrink-0">
                  <AvatarImage src={member.image} />
                  <AvatarFallback>{member.name.substring(0, 2).toUpperCase()}</AvatarFallback>
                </Avatar>
                <label
                  htmlFor={`avenue-member-${member.id}`}
                  className="flex-1 min-w-0 cursor-pointer"
                >
                  <p className="font-medium truncate">{member.name}</p>
                  {member.businessName && (
                    <p className="text-xs text-muted-foreground truncate">{member.businessName}</p>
                  )}
                </label>
              </div>
            ))}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => void handleSave()} disabled={isSaving || isLoading || !avenue}>
            {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
